import { AsyncPipe, CommonModule } from '@angular/common';
import { Component, Inject, Injector, Optional, signal } from '@angular/core';
import { MAT_DIALOG_DATA, MatDialogModule, MatDialogRef } from '@angular/material/dialog';
import { SnackbarService } from '@vendasta/galaxy/snackbar-service';
import {
  combineLatest,
  firstValueFrom,
  Observable,
  of,
  catchError,
  map,
  shareReplay,
  take,
  tap,
  switchMap,
} from 'rxjs';
import { CrmModelDrivenFormComponent } from '../model-driven-form';
import {
  CrmDependencies,
  CrmInjectionToken,
  CrmObjectDependencies,
  CrmObjectInjectionToken,
  FieldConfigInterface,
  FieldInterface,
  FieldOptionsInjectionToken as ModelDrivenFormFieldOptionsInjectionToken,
  FormDefaultInputValue,
  LoadingStatus,
  modelDrivenFormFieldOptionsInjectionTokenGenerator,
  ObjectType,
  PageAnalyticsInjectionToken,
  pageAnalyticsInjectionTokenGenerator,
  SavingStatus,
} from '../../tokens-and-interfaces';
import { CrmFormService, CrmObjectManagementTableChangesService, CRMTrackingService } from '../../shared-services';
import { SNACKBAR_DURATION } from '../../constants';
import { TranslateByObjectTypeService, TranslationModule } from '../../i18n';
import { CrmObjectInterface } from '@vendasta/crm';
import { getObjectDependencies } from '../../utils';

export interface CrmCreateCrmObjectModalData {
  objectType: ObjectType;
}

export interface CrmCreateCrmObjectModalResult {
  crmObject?: CrmObjectInterface;
}

export function createCrmObjectModalProviders(objectType: ObjectType, parentInjector: Injector): Injector {
  return Injector.create({
    providers: [
      {
        provide: ModelDrivenFormFieldOptionsInjectionToken,
        useFactory: modelDrivenFormFieldOptionsInjectionTokenGenerator(objectType),
      },
      {
        provide: PageAnalyticsInjectionToken,
        useFactory: pageAnalyticsInjectionTokenGenerator(objectType),
      },
    ],
    parent: parentInjector,
  });
}

@Component({
  // requires selector to prevent ID collision
  selector: 'crm-create-crm-object-modal',
  templateUrl: './create-crm-object-modal.component.html',
  imports: [CommonModule, TranslationModule, CrmModelDrivenFormComponent, MatDialogModule, AsyncPipe],
})
export class CrmCreateCrmObjectModalComponent {
  protected readonly objectType: ObjectType = this.data.objectType;
  protected readonly objectSubtype$: Observable<string> = this.crmObjectDependencies?.objectSubtype$ ?? of('');
  protected readonly loadingStatus = signal(LoadingStatus.LOADING);
  protected readonly savingStatus = signal(SavingStatus.NOT_SAVING);

  protected readonly fieldConfig$: Observable<FieldConfigInterface>;

  constructor(
    public dialogRef: MatDialogRef<CrmCreateCrmObjectModalComponent, CrmCreateCrmObjectModalResult>,
    @Inject(MAT_DIALOG_DATA) public data: CrmCreateCrmObjectModalData,
    @Inject(CrmInjectionToken) private readonly config: CrmDependencies,
    private readonly crmFormService: CrmFormService,
    private readonly snackService: SnackbarService,
    private readonly trackingService: CRMTrackingService,
    private readonly translateByObjectTypeService: TranslateByObjectTypeService,
    private readonly crmObjectManagementTableChangesService: CrmObjectManagementTableChangesService,
    @Optional()
    @Inject(CrmObjectInjectionToken)
    private readonly crmObjectDependencies: CrmObjectDependencies,
  ) {
    const fieldSchemas$ = this.objectSubtype$.pipe(
      switchMap((subtype) => {
        return combineLatest([
          this.crmFormService.listAllFormFields$(this.objectType, subtype),
          this.defaultInputValues$(),
        ]);
      }),
      map(([fieldSchemas, defaultInputValues]) => {
        return fieldSchemas.map((fieldSchema) => {
          const defaultInputValue = defaultInputValues.find(
            (inputValue) => inputValue.fieldId === fieldSchema.fieldId && !!inputValue.value,
          );
          if (defaultInputValue) {
            fieldSchema.value = defaultInputValue.value;
            fieldSchema.hasDefaultValue = true;
          }
          return fieldSchema;
        });
      }),
      take(1),
    );
    const fieldGroups$ = this.crmFormService.listAllObjectFormFieldGroups$(this.objectType, this.objectSubtype$);
    this.fieldConfig$ = combineLatest([fieldSchemas$, fieldGroups$]).pipe(
      tap(([fieldSchemas, fieldGroups]) => {
        const empty = fieldSchemas.length === 0 || fieldGroups.length === 0;
        this.loadingStatus.set(empty ? LoadingStatus.EMPTY : LoadingStatus.OK);
      }),
      map(([fieldSchemas, fieldGroups]) => {
        return {
          fieldSchemas: fieldSchemas,
          fieldGroups: fieldGroups,
        };
      }),
      catchError((error) => {
        this.loadingStatus.set(LoadingStatus.ERROR);
        this.errorSnackbar(error, 'ERRORS.GENERIC_MESSAGE');
        return of({} as FieldConfigInterface);
      }),
      shareReplay(1),
    );
  }

  async onSave(fields: FieldInterface[]): Promise<void> {
    const objectSubtype = await firstValueFrom(this.objectSubtype$);
    this.savingStatus.set(SavingStatus.SAVING);
    const createdObject = await firstValueFrom(
      this.crmFormService.validateAndCreateCrmObject$(this.objectType, fields, objectSubtype).pipe(
        catchError((error) => {
          this.errorSnackbar(error, 'ERRORS.GENERIC_MESSAGE');
          return of(null);
        }),
      ),
    );

    if (!createdObject) {
      this.savingStatus.set(SavingStatus.ERROR);
      this.trackingService.trackEvent(this.objectType, 'failed-add-crm-object');
    } else {
      this.crmObjectManagementTableChangesService.addCreatedCrmObject(this.objectType, createdObject, objectSubtype);
      this.savingStatus.set(SavingStatus.OK);
      this.trackingService.trackEvent(this.objectType, 'added-crm-object');
      this.dialogRef.close({ crmObject: createdObject });
    }
  }

  private defaultInputValues$(): Observable<FormDefaultInputValue[]> {
    if (this.crmObjectDependencies) {
      return this.crmObjectDependencies.formDefaultOnCreateInputValues$ ?? of([]);
    }
    return getObjectDependencies(this.objectType, this.config)?.formDefaultOnCreateInputValues$ ?? of([]);
  }

  private errorSnackbar(error: Error, defaultMessage: string) {
    let message: string | null = null;
    if (error && error.message) {
      message = error.message;
    }
    const translatedDefaultMessage = this.translateByObjectTypeService.getTranslationByObjectType(
      this.objectType,
      defaultMessage,
    );
    this.snackService.openErrorSnack(message ?? translatedDefaultMessage, {
      duration: SNACKBAR_DURATION,
    });
  }

  onCancel(): void {
    this.trackingService.trackEvent(this.objectType, 'cancelled-crm-add');
    this.dialogRef.close();
  }
}
